import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DECISIONS } from './bugDecisions';

/**
 * The four decisions as four buttons, deliberately not a dropdown.
 *
 * A dropdown of severities invites a shrug — pick the middle one and move on. Four
 * buttons that each say what will happen force the question the team actually has
 * to answer, and every one of them ends the conversation about this bug.
 *
 * @param {string} current - the disposition already set, so it can be shown as
 *   chosen rather than offered again
 * @param {Function} onDecide - (disposition, note) => void
 * @param {Function} onEmergency - emergency needs a bet and a reason, so it opens a
 *   dialog rather than deciding here
 */
const TriageBar = ({
  current,
  disabled = false,
  busy = false,
  showNote = true,
  onDecide,
  onEmergency,
}) => {
  const [note, setNote] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);

  const choose = (decision) => {
    if (decision.value === 'emergency') {
      onEmergency?.();
      return;
    }
    onDecide?.(decision.value, note.trim() || null);
    setNote('');
    setNoteOpen(false);
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {DECISIONS.map((decision) => {
          const chosen = current === decision.value;
          return (
            <Tooltip key={decision.value} title={decision.blurb}>
              <span>
                <Button
                  size="small"
                  variant={chosen ? 'contained' : 'outlined'}
                  color={decision.color === 'default' ? 'inherit' : decision.color}
                  startIcon={<decision.Icon fontSize="small" />}
                  disabled={disabled || busy || chosen}
                  onClick={() => choose(decision)}
                >
                  {decision.label}
                </Button>
              </span>
            </Tooltip>
          );
        })}
      </Stack>

      {showNote && !disabled && (
        <Box sx={{ mt: 1 }}>
          {!noteOpen ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ cursor: 'pointer', textDecoration: 'underline dotted' }}
              onClick={() => setNoteOpen(true)}
            >
              Add a note about why
            </Typography>
          ) : (
            <Collapse in>
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={2}
                autoFocus
                label="Why this decision"
                placeholder="Worth writing down for whoever reads this in three months."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Collapse>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TriageBar;
